import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import * as z from 'zod/v4-mini';
import { nonEmptyString } from '#layers/base';
import { AuthProtocol, defineAuthProvider } from '../../../types/auth-providers/define';

const RESERVED_PARAMS = [
	'redirect_uri',
	'state',
	'code_challenge',
	'code_challenge_method',
	'response_type',
	'client_id',
	'nonce',
] as const;

/**
 * Wire `config` shape (what the BE accepts/returns). Used for edit mode
 * (PATCH) where `clientSecret` may be omitted to keep the existing one.
 */
export const oauth2ConfigSchema = z.object({
	clientId: nonEmptyString(),
	clientSecret: z.optional(nonEmptyString()),
	authorizationEndpoint: z.url(),
	tokenEndpoint: z.url(),
	userinfoEndpoint: z.url(),
	scopes: z.array(z.string()).check(z.minLength(1, '$t:sso_providers.oauth2.scopes_required')),
	claimMappings: z.object({
		sub: nonEmptyString(),
		email: z.optional(nonEmptyString()),
	}),
	extraAuthorizeParams: z.optional(
		z.record(z.string(), z.string()).check(
			z.refine(
				(rec: Record<string, string> | undefined) =>
					!rec || Object.keys(rec).every((k) => !RESERVED_PARAMS.includes(k as typeof RESERVED_PARAMS[number])),
				{ error: '$t:sso_providers.oauth2.reserved_param_not_allowed' },
			),
		),
	),
});

export type OAuth2Config = z.infer<typeof oauth2ConfigSchema>;

/**
 * Create-mode wire schema. `clientSecret` is required.
 */
export const oauth2ConfigCreateSchema = z.object({
	...oauth2ConfigSchema.def.shape,
	clientSecret: nonEmptyString(),
});

/**
 * Form-side shape. `extraAuthorizeParams` is an array of `{ key, value }` rows
 * because `InputRepeater` produces an array. `transformOptions` folds it back
 * into a `Record<string, string>` for the wire.
 */
export const oauth2FormValuesSchema = z.object({
	clientId: nonEmptyString(),
	clientSecret: z.optional(nonEmptyString()),
	authorizationEndpoint: z.url(),
	tokenEndpoint: z.url(),
	userinfoEndpoint: z.url(),
	scopes: z.array(z.string()).check(z.minLength(1, '$t:sso_providers.oauth2.scopes_required')),
	claimMappings: z.object({
		sub: nonEmptyString(),
		email: z.optional(nonEmptyString()),
	}),
	extraAuthorizeParams: z.optional(z.array(z.object({
		key: z.string(),
		value: z.string(),
	}))),
});

export type OAuth2FormValues = z.infer<typeof oauth2FormValuesSchema>;

export const oauth2 = defineAuthProvider<OAuth2Config, OAuth2FormValues>({
	id: 'oauth2',
	name: '$t:sso_providers.oauth2.name',
	description: '$t:sso_providers.oauth2.description',
	protocol: AuthProtocol.OAuth2,
	icon: 'lucide:key-round',
	schema: oauth2ConfigCreateSchema,
	editSchema: oauth2ConfigSchema,
	options: ({ form }) => form([
		{ type: 'separator', label: '$t:sso_providers.oauth2.section.credentials' },
		{
			name: 'clientId',
			label: '$t:sso_providers.oauth2.client_id',
			description: '$t:sso_providers.oauth2.client_id_hint',
			block: BuiltinBlockIds.Input,
			required: true,
		},
		{
			name: 'clientSecret',
			label: '$t:sso_providers.oauth2.client_secret',
			description: '$t:sso_providers.oauth2.client_secret_hint',
			block: BuiltinBlockIds.InputPassword,
			required: true,
		},

		// All sections below are wrapped in a single collapsible group. Custom
		// OAuth 2 keeps it open by default (admin must fill these); presets
		// override `defaultOpen` to false because their values are prefilled
		// (see `presets/google.ts`).
		{
			type: 'group',
			label: '$t:auth.providers.new.provider_configuration_section',
			defaultOpen: true,
			collapsible: true,
			fields: [
				{ type: 'separator', label: '$t:sso_providers.oauth2.section.endpoints' },
				{
					name: 'authorizationEndpoint',
					label: '$t:sso_providers.oauth2.authorize_url',
					description: '$t:sso_providers.oauth2.authorize_url_hint',
					block: BuiltinBlockIds.Input,
					required: true,
					props: { type: 'url' },
				},
				{
					name: 'tokenEndpoint',
					label: '$t:sso_providers.oauth2.token_url',
					description: '$t:sso_providers.oauth2.token_url_hint',
					block: BuiltinBlockIds.Input,
					required: true,
					props: { type: 'url' },
				},
				{
					name: 'userinfoEndpoint',
					label: '$t:sso_providers.oauth2.userinfo_url',
					description: '$t:sso_providers.oauth2.userinfo_url_hint',
					block: BuiltinBlockIds.Input,
					required: true,
					props: { type: 'url' },
				},

				{ type: 'separator', label: '$t:sso_providers.oauth2.section.scopes' },
				{
					name: 'scopes',
					label: '$t:sso_providers.oauth2.scopes',
					description: '$t:sso_providers.oauth2.scopes_hint',
					block: BuiltinBlockIds.InputTags,
					required: true,
				},

				{ type: 'separator', label: '$t:sso_providers.oauth2.section.claim_mapping' },
				{
					name: 'claimMappings.sub',
					label: '$t:sso_providers.oauth2.claim_sub',
					description: '$t:sso_providers.oauth2.claim_sub_hint',
					block: BuiltinBlockIds.Input,
					required: true,
				},
				{
					name: 'claimMappings.email',
					label: '$t:sso_providers.oauth2.claim_email',
					description: '$t:sso_providers.oauth2.claim_email_hint',
					block: BuiltinBlockIds.Input,
				},

				{ type: 'separator', label: '$t:sso_providers.oauth2.section.advanced' },
				{
					name: 'extraAuthorizeParams',
					label: '$t:sso_providers.oauth2.extra_params',
					description: '$t:sso_providers.oauth2.extra_params_hint',
					block: BuiltinBlockIds.InputRepeater,
					props: {
						schema: [
							{
								name: 'key',
								label: '$t:sso_providers.oauth2.param_key',
								description: '$t:sso_providers.oauth2.param_key_hint',
								block: BuiltinBlockIds.Input,
								required: true,
							},
							{
								name: 'value',
								label: '$t:sso_providers.oauth2.param_value',
								description: '$t:sso_providers.oauth2.param_value_hint',
								block: BuiltinBlockIds.Input,
								required: true,
							},
						],
						// Closed-row label shows the key only. Display templates are
						// collection-bound and don't fit ad-hoc `{ key, value }` rows;
						// `displayBlock.valueKey` is the cleanest fallback supported by
						// InputRepeater. TODO(FE-SSO-2): build a tiny `key = value` display
						// block for full row preview.
						displayBlock: {
							id: BuiltinBlockIds.DisplayFormattedValue,
							valueKey: 'key',
							props: { fontFamily: 'mono' },
						},
					},
				},
			],
		},
	]),
	transformOptions: (form): OAuth2Config => {
		const config: OAuth2Config = {
			clientId: form.clientId,
			authorizationEndpoint: form.authorizationEndpoint,
			tokenEndpoint: form.tokenEndpoint,
			userinfoEndpoint: form.userinfoEndpoint,
			scopes: form.scopes,
			claimMappings: form.claimMappings.email
				? form.claimMappings
				: { sub: form.claimMappings.sub },
		};

		if (form.clientSecret !== undefined) config.clientSecret = form.clientSecret;

		if (form.extraAuthorizeParams && form.extraAuthorizeParams.length > 0) {
			config.extraAuthorizeParams = Object.fromEntries(
				form.extraAuthorizeParams.map((p) => [p.key, p.value]),
			);
		}

		return config;
	},
	// Inverse for edit prefill. `clientSecret` stays empty — BE never returns it,
	// and the form's `clientSecret_keep_hint` placeholder explains the convention.
	parseInitialValues: (config): Partial<OAuth2FormValues> => ({
		clientId: config.clientId,
		clientSecret: '',
		authorizationEndpoint: config.authorizationEndpoint,
		tokenEndpoint: config.tokenEndpoint,
		userinfoEndpoint: config.userinfoEndpoint,
		scopes: config.scopes,
		claimMappings: config.claimMappings,
		extraAuthorizeParams: config.extraAuthorizeParams
			? Object.entries(config.extraAuthorizeParams).map(([key, value]) => ({ key, value }))
			: [],
	}),
});
